import {Injectable} from '@angular/core';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {IsEmptyString, IsEmptyVar} from '../../defines/common.func';
import {PROVERB_PAGE_SIZE} from '../../defines/desktop.const';
import {PAGE, PROVERB_API_URL, PROVERB_DETAIL_API_URL, PROVERB_WORD_API_URL} from '../../defines/protocol';
import {SearchParam} from '../../defines/client.const';
import {Proverb} from '../../models/proverb.model';
import {generateUrlWithQueryParams, getQueryParamsFromUrl, NormalizeSearchKeyword} from "../../defines/client.func";
import {PageLoader} from '../../interfaces/page-loader';
import {ApiService} from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class ProverbService implements PageLoader<Proverb, string> {
  protected urlApi = PROVERB_API_URL;

  protected curPage: number | null = null;
  protected totalCount: number | null = null;
  protected listProverb: Array<Proverb> | null = null;
  protected mapProverb: Map<string, Proverb> | null = null;
  protected nextUrl: string | null = null
  protected prevUrl: string | null = null;
  protected load$: Observable<Array<Proverb>> | null = null;
  protected loadDetail$: Observable<Proverb> | null = null;


  constructor(
    protected api: ApiService,
  ) { }


  /******************************************************************************
   * PageLoader<SimpleProverb> 구현부
   *****************************************************************************/
  public getList(): Array<Proverb> | null {
    return this.listProverb;
  }
  public getCurPage(): number | null {
    return this.curPage;
  }
  public getTotalCount(): number | null {
    return this.totalCount;
  }
  public IsFirstPage(): boolean {
    return IsEmptyString(this.prevUrl);
  }
  public IsLastPage(): boolean {
    return IsEmptyString(this.nextUrl);
  }
  public IsLoading(): boolean {
    return this.load$ !== null;
  }
  public getById(id: string): Proverb | null {
    return this.mapProverb?.get(id) ?? null;
  }

  public ResetList(param?: SearchParam): Observable<number> {
    this.listProverb = null;
    this.mapProverb = null;
    this.curPage = 0;
    this.totalCount = null;
    this.nextUrl = this.urlApi;
    this.prevUrl = null;

    return new Observable<number>(observer => {

      if (param) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        this.nextUrl = generateUrlWithQueryParams(
          this.urlApi + (param.word_id ? param.word_id + '/' : ''),
          {
            page_size: PROVERB_PAGE_SIZE,
            ...('index' in param ? {index: param.index} : {}),
            ...(keyword ? {search: keyword} : {}),
            ...('page' in param ? {page: param.page} : {}),
        });
      }
      else {
        this.nextUrl = this.urlApi;
      }

      console.log('ProverbService::ResetList() nextUrl =', this.nextUrl);

      // console.log('ProverbService::ResetList()', this.urlApi, this.nextUrl);
      observer.next(1);
      observer.complete();
    });
  }

  public GetNextList(): Observable<Array<Proverb>> {

    if (this.nextUrl === null) {
      return throwError({error: 'No more next pages in ProverbList'});
    }

    const nextUrl = this.nextUrl;
    return this.getUrlList(nextUrl).pipe(
      finalize(
        () => {
          const param = getQueryParamsFromUrl(nextUrl);
          this.curPage = 'page' in param ? parseInt(param['page'], 10): 1;
        }
      )
    );
  }

  public GetPreList(): Observable<Array<Proverb>> {

    if (this.prevUrl === null) {
      return throwError({error: 'No more pre pages in ProverbList'});
    }

    const prevUrl = this.prevUrl;
    return this.getUrlList(prevUrl).pipe(
      finalize(
        () => {
          const param = getQueryParamsFromUrl(prevUrl)
          this.curPage = 'page' in param ? parseInt(param['page'], 10) : 1;
        }
      )
    );
  }

  protected getUrlList(url: string): Observable<Array<Proverb>> {
    // console.log('ProverbService::getUrlList()', urls);

    if (this.load$) {
      return this.load$;
    }

    return this.load$ = this.api.Get(url).pipe(
      catchError(err => {
        if (err?.status === 404) {
          this.totalCount = 0;
        }
        throw err;
      }),
      finalize(() => {
        this.load$ = null;
      } ),
      map((data: PAGE) => {
        console.log('ProverbService::getUrlList.api.Get data =', data);

        if (this.totalCount === null) {
          this.totalCount = data.count;
        }
        else if (this.totalCount !== data.count) {
          console.error('[error]' + this.urlApi, data );
        }

        this.listProverb = new Array<Proverb>();
        this.mapProverb = new Map<string, Proverb>();

        data.results.forEach(value => {
          const proverb = new Proverb(value);
          this.listProverb?.push(proverb);
          this.mapProverb?.set(proverb.id, proverb);
        });

        this.nextUrl = data.next ?? null;
        this.prevUrl = data.previous ?? null;

        return this.listProverb;
      })
    );
  }


  /******************************************************************************
   * 상세 속담 정보 획득
   * 위쪽의 목록에는 간섭이 없도록 처리하는 것이 좋다.
   * TODO: 로딩중인 것을 처리. (예외상황 처리 추가해야 함)
   *****************************************************************************/
  // tslint:disable-next-line:variable-name
  public GetDetail(proverb_id: string): Observable<Proverb> {
    const url = PROVERB_DETAIL_API_URL + proverb_id + '/';
    console.log('ProverbService::GetDetail() url =', url);

    return this.loadDetail$ = this.api.Get(url).pipe(
      finalize(() => this.loadDetail$ = null),
      map(value => {
        const out = new Proverb(value);
        console.log('ProverbService::GetDetail data = ', value, out);
        return out;
      })
    );
  }

}
